<template>
  <app-framework>
    <header class="container">
      <bread-crumbs :list="[
        { name: $t('home'), link: '/' },
        { name: $t('store_listing'), link: '/stores' },
        { name: $t($route.params.id === 'new' ? 'store_form_new' : 'store_form_edit') }
      ]" />
      <h1>{{ $t($route.params.id === 'new' ? 'store_form_new' : 'store_form_edit') }}</h1>
    </header>
    <article class="container framework-slot">
      <div v-if="store.loading">
        <loading-state/>
      </div>
      <div v-else-if="store.error" class="alert alert-danger" role="alert">
        {{ $t('failed_retrieve_data_from_server') }}
        <button class="btn btn-link" @click="tryAgain">{{ $t('try_again') }}</button>
      </div>
      <form v-else v-on:submit.prevent="saveStore">
        <div v-if="validationError" class="alert alert-danger" role="alert">
          {{ validationError }}
        </div>
        <form-page>
          <div class="form-row">
            <form-group class="col-12 required" :label="$t('name')" label-for="name" prepend-icon="tag"
                        :invalid-feedback="validationErrors.name">
              <input id="name" autocomplete="store-name" type="text" class="form-control" v-model="store.node.name" v-focus>
            </form-group>
            <form-group class="col-sm-6 required" :label="$t('menu')" label-for="menu-id"
                        prepend-icon="table"
                        :invalid-feedback="validationErrors.menuId">
              <select id="menu-id" class="custom-select" v-model="store.node.menuId">
                <option v-for="menu in menus.nodes" :key="menu.id" :value="menu.id">{{menu.name}}</option>
              </select>
            </form-group>
            <form-group class="col-sm-6" :label="$t('phone')" label-for="phone" prepend-icon="phone"
                        :invalid-feedback="validationErrors.phone">
              <the-mask
                  id="phone"
                  v-model="store.node.phone"
                  autocomplete="store-phone"
                  type="tel"
                  class="form-control"
                  :masked="true"
                  :mask="$getMask('phone')"
              />
            </form-group>
            <form-group class="col-12 required" :label="$t('store_code')" label-for="store-code"
                        :invalid-feedback="validationErrors.code" :description="$t('warning_store_code')"
                        :prepend-text="`...cardapio.app.br/`">
              <input id="store-code" type="text" class="form-control" v-model="store.node.code">
            </form-group>

            <form-group class="col-md-6" :label="$t('minimum_order_value')" label-for="minimum-order-value"
                        prepend-icon="hashtag"
                        :invalid-feedback="validationErrors.minimumOrderValue">
                <money id="fixed-delivery-fee"
                       v-model="store.node.minimumOrderValue"
                       v-bind="$getMoney()"
                       class="form-control"
                />
            </form-group>
            <form-group class="col-md-6" :label="$t('preparation_time')" label-for="preparation-time" prepend-icon="hourglass-start"
                        :append-text="$t('preparation_time_append_text')" :description="$t('preparation_time_description')" :invalid-feedback="validationErrors.preparationTime">
              <input
                  id="preparation-time"
                  v-model="store.node.preparationTimeInMinutes"
                  type="number"
                  step="1"
                  class="form-control"
              />
            </form-group>
            <form-group class="col-sm-6 col-md-6 required" :label="$t('accept_delivery_order')" label-for="accept-delivery-order"
                        prepend-icon="motorcycle"
                        :invalid-feedback="validationErrors.acceptDeliveryOrder">
              <select id="accept-delivery-order" class="custom-select" v-model="store.node.acceptDeliveryOrder">
                <option :value="true">{{ $t('accept_delivery_order_yes') }}</option>
                <option :value="false">{{ $t('accept_delivery_order_no') }}</option>
              </select>
            </form-group>
            <form-group class="col-sm-6 col-md-6 required" :label="$t('accept_pickup_order')" label-for="accept-pickup-order"
                        prepend-icon="hand-holding-box"
                        :invalid-feedback="validationErrors.acceptPickupOrder">
              <select id="accept-pickup-order" class="custom-select" v-model="store.node.acceptPickupOrder">
                <option :value="true">{{ $t('accept_pickup_order_yes') }}</option>
                <option :value="false">{{ $t('accept_pickup_order_no') }}</option>
              </select>
            </form-group>

          </div>

          <fieldset>
            <legend>
              {{ $t('payment_methods') }}
            </legend>
            <div class="delivery-named-areas-header form-row">
              <div class="col">
                <label>{{ $t('payment_methods_options') }}</label>
              </div>
              <div class="col-auto">
                <button type="button" class="btn btn-secondary btn-sm"
                        @click="store.node.paymentMethods.push({name: '', askNeedChange: false, priceChangeOption: 'NO_CHANGE', fixedPriceIncrease: 0, percentagePriceIncrease: 0})">
                  {{ $t('payment_method_add') }}
                </button>
              </div>
            </div>
            <div class="delivery-named-areas">
              <div v-for="(paymentMethod, index) in store.node.paymentMethods"
                   :key="`payment-method-${index}`">
                <div class="form-row">
                  <form-group class="col-sm-6" :label="$t('payment_method_name')"
                              :label-for="`payment-method-${index}-name`"
                              :invalid-feedback="validationErrors[`paymentMethod${index}Name`]">
                    <input :id="`payment-method-${index}-name`" type="text" class="form-control" v-model="paymentMethod.name">
                  </form-group>
                  <form-group class="col-sm-6"
                              :label="$t('payment_method_ask_need_change')"
                              :label-for="`payment-method-${index}-ask-need-change`"
                              :invalid-feedback="validationErrors[`paymentMethod${index}AskNeedChange`]">
                    <select :id="`payment-method-${index}-ask-need-change`" class="custom-select" v-model="paymentMethod.askNeedChange">
                      <option :value="false">{{ $t('payment_method_ask_need_change_no') }}</option>
                      <option :value="true">{{ $t('payment_method_ask_need_change_yes') }}</option>
                    </select>
                  </form-group>
                  <form-group :class="{'col-sm-12': paymentMethod.priceChangeOption === 'NO_CHANGE', 'col-sm-6': paymentMethod.priceChangeOption !== 'NO_CHANGE'}"
                              :label="$t('payment_method_price_change_option')"
                              :label-for="`payment-method-${index}-price-change-option`"
                              :invalid-feedback="validationErrors[`paymentMethod${index}PriceChangeOption`]">
                    <select :id="`payment-method-${index}-price-change-option`" class="custom-select" v-model="paymentMethod.priceChangeOption">
                      <option value="NO_CHANGE">{{ $t('payment_method_price_change_option_no_change') }}</option>
                      <option value="FIXED_DISCOUNT">{{ $t('payment_method_price_change_option_fixed_discount') }}</option>
                      <option value="PERCENTAGE_DISCOUNT">{{ $t('payment_method_price_change_option_percentage_discount') }}</option>
                      <option value="FIXED_INCREASE">{{ $t('payment_method_price_change_option_fixed_increase') }}</option>
                      <option value="PERCENTAGE_INCREASE">{{ $t('payment_method_price_change_option_percentage_increase') }}</option>
                    </select>
                  </form-group>
                  <form-group v-if="['FIXED_DISCOUNT', 'FIXED_INCREASE'].indexOf(paymentMethod.priceChangeOption) !== -1" class="col-sm-6" :label="$t('payment_method_fixed_price_increase')"
                              :label-for="`payment-method-${index}-fixed-price-increase`"
                              :invalid-feedback="validationErrors[`paymentMethod${index}FixedPriceIncrease`]">
                    <money
                        :id="`payment-method-${index}-fixed-price-increase`"
                        v-model="paymentMethod.fixedPriceIncrease"
                        v-bind="$getMoney()"
                        class="form-control"
                    />
                  </form-group>
                  <form-group v-if="['PERCENTAGE_DISCOUNT', 'PERCENTAGE_INCREASE'].indexOf(paymentMethod.priceChangeOption) !== -1" class="col-sm-6" :label="$t('payment_method_percentage_price_increase')"
                              :label-for="`payment-method-${index}-percentage-price-increase`"
                              :invalid-feedback="validationErrors[`paymentMethod${index}PercentagePriceIncrease`]">
                    <the-mask
                        :id="`payment-method-${index}-percentage-price-increase`"
                        v-model="paymentMethod.percentagePriceIncrease"
                        type="tel"
                        class="form-control"
                        :masked="false"
                        :mask="['#%', '##%', '###%']"
                    />
                  </form-group>
                </div>
                <button v-if="store.node.paymentMethods.length > 1" type="button"
                        class="btn btn-sm btn-outline-danger" @click="store.node.paymentMethods.splice(index, 1)">
                  {{ $t('payment_method_remove') }}
                </button>
              </div>
            </div>

          </fieldset>

          <fieldset>
            <legend>
              {{ $t('delivery_fee') }}
            </legend>
            <div class="form-row">
              <form-group class="required" :class="{'col-12': store.node.deliveryFeeType !== 'FIXED', 'col-6': store.node.deliveryFeeType === 'FIXED'}"
                          :label="$t('delivery_fee_type')" label-for="delivery-fee-type" prepend-icon="dollar-sign" :invalid-feedback="validationErrors.deliveryFeeType">
                <select id="delivery-fee-type" class="custom-select" v-model="store.node.deliveryFeeType">
                  <option v-for="deliveryFeeType in deliveryFeeTypes" :key="`delivery-fee-${deliveryFeeType}`"
                          :value="deliveryFeeType">{{ $t(deliveryFeeType) }}
                  </option>
                </select>
              </form-group>
              <form-group v-if="store.node.deliveryFeeType === 'FIXED'" class="col-6 required" :label="$t('delivery_fee_fixed')" label-for="fixed-delivery-fee"
                          prepend-icon="hashtag" :invalid-feedback="validationErrors.deliveryFeeFixed">
                <money
                    id="fixed-delivery-fee"
                    v-model="store.node.deliveryFeeFixed"
                    v-bind="$getMoney()"
                    class="form-control"
                />
              </form-group>
            </div>

            <div v-if="store.node.deliveryFeeType === 'BY_NAMED_AREA'" class="delivery-named-areas-header form-row">
              <div class="col">
                <label>{{ $t('delivery_named_areas') }}</label>
              </div>
              <div class="col-auto">
                <button type="button" class="btn btn-secondary btn-sm"
                        @click="store.node.deliveryFeeByNamedArea.push({name: '', price: 0})">
                  {{ $t('delivery_named_area_add') }}
                </button>
              </div>
            </div>
            <div v-if="store.node.deliveryFeeType === 'BY_NAMED_AREA'" class="delivery-named-areas">
              <div v-for="(deliveryNamedArea, index) in store.node.deliveryFeeByNamedArea"
                   :key="`delivery-fee-by-named-area-${index}`">
                <div class="form-row">
                  <form-group class="col-6" :label="$t('delivery_named_area_name')"
                              :label-for="`delivery-fee-by-named-area-${index}-name`" prepend-icon="tag"
                              :invalid-feedback="validationErrors[`deliveryFeeByNamedArea${index}Name`]">
                    <input :id="`delivery-fee-by-named-area-${index}-name`" type="text" class="form-control" v-model="deliveryNamedArea.name">
                  </form-group>
                  <form-group class="col-6" :label="$t('delivery_named_area_price')"
                              :label-for="`delivery-fee-by-named-area-${index}-price`" prepend-icon="hashtag"
                              :invalid-feedback="validationErrors[`deliveryFeeByNamedArea${index}Price`]">
                    <money
                        :id="`delivery-fee-by-named-area-${index}-price`"
                        v-model="deliveryNamedArea.price"
                        v-bind="$getMoney()"
                        class="form-control"
                    />
                  </form-group>
                </div>
                <button v-if="store.node.deliveryFeeByNamedArea.length > 1" type="button"
                        class="btn btn-sm btn-outline-danger" @click="store.node.deliveryFeeByNamedArea.splice(index, 1)">
                  {{ $t('delivery_named_area_remove') }}
                </button>
              </div>
            </div>
            <div class="form-row">
              <form-group class="col-12" :label="$t('free_delivery_starting_from')" label-for="free-delivery-starting-from"
                          prepend-icon="hashtag" :invalid-feedback="validationErrors.freeDeliveryStartingFrom" :description="$t('free_delivery_starting_from_description')">
                <money
                    id="free-delivery-starting-from"
                    v-model="store.node.freeDeliveryStartingFrom"
                    v-bind="$getMoney()"
                    class="form-control"
                />
              </form-group>
            </div>

          </fieldset>

          <fieldset>
            <legend>
              {{ $t('address') }}
            </legend>
            <div class="form-row">
              <form-group class="col-12 col-sm-9" :label="$t('address_line1')" label-for="address-line1"
                          :invalid-feedback="validationErrors.addressLine1">
                <input id="address-line1" autocomplete="store-address-line1" type="text" class="form-control"
                       v-model="store.node.address.line1">
              </form-group>
              <form-group class="col-12 col-sm-3" :label="$t('address_house_number')" label-for="address-house-number"
                          :invalid-feedback="validationErrors.addressHouseNumber">
                <input id="address-house-number" autocomplete="store-address-house-number" type="text" class="form-control"
                       v-model="store.node.address.houseNumber">
              </form-group>
              <form-group class="col-12 col-sm-4" :label="$t('address_line2')" label-for="address-line2"
                          :invalid-feedback="validationErrors.addressLine2">
                <input id="address-line2" autocomplete="store-address-line2" type="text" class="form-control"
                       v-model="store.node.address.line2">
              </form-group>
              <form-group class="col-12 col-sm-4" :label="$t('address_level3')" label-for="address-level3"
                          :invalid-feedback="validationErrors.addressLevel3">
                <input id="address-level3" autocomplete="store-address-level3" type="text" class="form-control"
                       v-model="store.node.address.level3">
              </form-group>
              <form-group class="col-12 col-sm-4" :label="$t('postal_code')" label-for="address-postal-code"
                          :invalid-feedback="validationErrors.addressPostalCode">
                <input id="address-postal-code" autocomplete="store-address-postal-code" type="text" class="form-control"
                       v-model="store.node.address.postalCode">
              </form-group>
              <form-group class="col-12 col-sm-6 required" :label="$t('address_level2')" label-for="address-level2"
                          :invalid-feedback="validationErrors.addressLevel2">
                <input id="address-level2" autocomplete="store-address-level2" type="text" class="form-control"
                       v-model="store.node.address.level2">
              </form-group>
              <form-group class="col-12 col-sm-6 required" :label="$t('address_level1')" label-for="address-level1"
                          :invalid-feedback="validationErrors.addressLevel1">
                <input id="address-level1" autocomplete="store-address-level1" type="text" class="form-control"
                       v-model="store.node.address.level1">
              </form-group>
            </div>
          </fieldset>

          <fieldset>
            <legend>
              {{ $t('opening_hours') }}
            </legend>

            <div class="opening-hours-header form-row">
              <div class="col">
                <label>{{ $t('sunday') }}</label>
              </div>
              <div class="col-auto">
                <button type="button" class="btn btn-secondary btn-sm"
                        @click="store.node.openingHours.sunday.push({from: '00:00', to: '00:00'})">
                  {{ $t('opening_hours_add') }}
                </button>
              </div>
            </div>
            <div class="opening-hours">
              <div v-for="(openingHour, index) in store.node.openingHours.sunday"
                   :key="`opening-hours-sunday-${index}-from`">
                <div class="form-row">
                  <form-group class="col-6" :label="$t('opening_hours_from')"
                              :label-for="`opening-hours-sunday-${index}-from`" prepend-icon="hourglass-start"
                              :invalid-feedback="validationErrors[`openingHoursSunday${index}From`]">
                    <select :id="`opening-hours-sunday-${index}-from`" class="custom-select" v-model="openingHour.from">
                      <option v-for="hour in hours" :key="hour" :value="hour">{{ hour }}</option>
                    </select>
                  </form-group>
                  <form-group class="col-6" :label="$t('opening_hours_to')"
                              :label-for="`opening-hours-sunday-${index}-to`" prepend-icon="hourglass-end"
                              :invalid-feedback="validationErrors[`openingHoursSunday${index}To`]">
                    <select :id="`opening-hours-sunday-${index}-to`" class="custom-select" v-model="openingHour.to">
                      <option v-for="hour in hours" :key="hour" :value="hour">{{ hour }}</option>
                    </select>
                  </form-group>
                </div>
                <button v-if="store.node.openingHours.sunday.length > 1" type="button"
                        class="btn btn-sm btn-outline-danger" @click="store.node.openingHours.sunday.splice(index, 1)">
                  {{ $t('opening_hours_remove') }}
                </button>
              </div>
            </div>

            <div class="opening-hours-header form-row">
              <div class="col">
                <label>{{ $t('monday') }}</label>
              </div>
              <div class="col-auto">
                <button type="button" class="btn btn-secondary btn-sm"
                        @click="store.node.openingHours.monday.push({from: '00:00', to: '00:00'})">
                  {{ $t('opening_hours_add') }}
                </button>
              </div>
            </div>
            <div class="opening-hours">
              <div v-for="(openingHour, index) in store.node.openingHours.monday"
                   :key="`opening-hours-monday-${index}-from`">
                <div class="form-row">
                  <form-group class="col-6" :label="$t('opening_hours_from')"
                              :label-for="`opening-hours-monday-${index}-from`" prepend-icon="hourglass-start"
                              :invalid-feedback="validationErrors[`openingHoursMonday${index}From`]">
                    <select :id="`opening-hours-monday-${index}-from`" class="custom-select" v-model="openingHour.from">
                      <option v-for="hour in hours" :key="hour" :value="hour">{{ hour }}</option>
                    </select>
                  </form-group>
                  <form-group class="col-6" :label="$t('opening_hours_to')"
                              :label-for="`opening-hours-monday-${index}-to`" prepend-icon="hourglass-end"
                              :invalid-feedback="validationErrors[`openingHoursMonday${index}To`]">
                    <select :id="`opening-hours-monday-${index}-to`" class="custom-select" v-model="openingHour.to">
                      <option v-for="hour in hours" :key="hour" :value="hour">{{ hour }}</option>
                    </select>
                  </form-group>
                </div>
                <button v-if="store.node.openingHours.monday.length > 1" type="button"
                        class="btn btn-sm btn-outline-danger" @click="store.node.openingHours.monday.splice(index, 1)">
                  {{ $t('opening_hours_remove') }}
                </button>
              </div>
            </div>

            <div class="opening-hours-header form-row">
              <div class="col">
                <label>{{ $t('tuesday') }}</label>
              </div>
              <div class="col-auto">
                <button type="button" class="btn btn-secondary btn-sm"
                        @click="store.node.openingHours.tuesday.push({from: '00:00', to: '00:00'})">
                  {{ $t('opening_hours_add') }}
                </button>
              </div>
            </div>
            <div class="opening-hours">
              <div v-for="(openingHour, index) in store.node.openingHours.tuesday"
                   :key="`opening-hours-tuesday-${index}-from`">
                <div class="form-row">
                  <form-group class="col-6" :label="$t('opening_hours_from')"
                              :label-for="`opening-hours-tuesday-${index}-from`" prepend-icon="hourglass-start"
                              :invalid-feedback="validationErrors[`openingHoursTuesday${index}From`]">
                    <select :id="`opening-hours-tuesday-${index}-from`" class="custom-select" v-model="openingHour.from">
                      <option v-for="hour in hours" :key="hour" :value="hour">{{ hour }}</option>
                    </select>
                  </form-group>
                  <form-group class="col-6" :label="$t('opening_hours_to')"
                              :label-for="`opening-hours-tuesday-${index}-to`" prepend-icon="hourglass-end"
                              :invalid-feedback="validationErrors[`openingHoursTuesday${index}To`]">
                    <select :id="`opening-hours-tuesday-${index}-to`" class="custom-select" v-model="openingHour.to">
                      <option v-for="hour in hours" :key="hour" :value="hour">{{ hour }}</option>
                    </select>
                  </form-group>
                </div>
                <button v-if="store.node.openingHours.tuesday.length > 1" type="button"
                        class="btn btn-sm btn-outline-danger" @click="store.node.openingHours.tuesday.splice(index, 1)">
                  {{ $t('opening_hours_remove') }}
                </button>
              </div>
            </div>

            <div class="opening-hours-header form-row">
              <div class="col">
                <label>{{ $t('wednesday') }}</label>
              </div>
              <div class="col-auto">
                <button type="button" class="btn btn-secondary btn-sm"
                        @click="store.node.openingHours.wednesday.push({from: '00:00', to: '00:00'})">
                  {{ $t('opening_hours_add') }}
                </button>
              </div>
            </div>
            <div class="opening-hours">
              <div v-for="(openingHour, index) in store.node.openingHours.wednesday"
                   :key="`opening-hours-wednesday-${index}-from`">
                <div class="form-row">
                  <form-group class="col-6" :label="$t('opening_hours_from')"
                              :label-for="`opening-hours-wednesday-${index}-from`" prepend-icon="hourglass-start"
                              :invalid-feedback="validationErrors[`openingHoursWednesday${index}From`]">
                    <select :id="`opening-hours-wednesday-${index}-from`" class="custom-select" v-model="openingHour.from">
                      <option v-for="hour in hours" :key="hour" :value="hour">{{ hour }}</option>
                    </select>
                  </form-group>
                  <form-group class="col-6" :label="$t('opening_hours_to')"
                              :label-for="`opening-hours-wednesday-${index}-to`" prepend-icon="hourglass-end"
                              :invalid-feedback="validationErrors[`openingHoursWednesday${index}To`]">
                    <select :id="`opening-hours-wednesday-${index}-to`" class="custom-select" v-model="openingHour.to">
                      <option v-for="hour in hours" :key="hour" :value="hour">{{ hour }}</option>
                    </select>
                  </form-group>
                </div>
                <button v-if="store.node.openingHours.wednesday.length > 1" type="button"
                        class="btn btn-sm btn-outline-danger" @click="store.node.openingHours.wednesday.splice(index, 1)">
                  {{ $t('opening_hours_remove') }}
                </button>
              </div>
            </div>

            <div class="opening-hours-header form-row">
              <div class="col">
                <label>{{ $t('thursday') }}</label>
              </div>
              <div class="col-auto">
                <button type="button" class="btn btn-secondary btn-sm"
                        @click="store.node.openingHours.thursday.push({from: '00:00', to: '00:00'})">
                  {{ $t('opening_hours_add') }}
                </button>
              </div>
            </div>
            <div class="opening-hours">
              <div v-for="(openingHour, index) in store.node.openingHours.thursday"
                   :key="`opening-hours-thursday-${index}-from`">
                <div class="form-row">
                  <form-group class="col-6" :label="$t('opening_hours_from')"
                              :label-for="`opening-hours-thursday-${index}-from`" prepend-icon="hourglass-start"
                              :invalid-feedback="validationErrors[`openingHoursThursday${index}From`]">
                    <select :id="`opening-hours-thursday-${index}-from`" class="custom-select" v-model="openingHour.from">
                      <option v-for="hour in hours" :key="hour" :value="hour">{{ hour }}</option>
                    </select>
                  </form-group>
                  <form-group class="col-6" :label="$t('opening_hours_to')"
                              :label-for="`opening-hours-thursday-${index}-to`" prepend-icon="hourglass-end"
                              :invalid-feedback="validationErrors[`openingHoursThursday${index}To`]">
                    <select :id="`opening-hours-thursday-${index}-to`" class="custom-select" v-model="openingHour.to">
                      <option v-for="hour in hours" :key="hour" :value="hour">{{ hour }}</option>
                    </select>
                  </form-group>
                </div>
                <button v-if="store.node.openingHours.thursday.length > 1" type="button"
                        class="btn btn-sm btn-outline-danger" @click="store.node.openingHours.thursday.splice(index, 1)">
                  {{ $t('opening_hours_remove') }}
                </button>
              </div>
            </div>

            <div class="opening-hours-header form-row">
              <div class="col">
                <label>{{ $t('friday') }}</label>
              </div>
              <div class="col-auto">
                <button type="button" class="btn btn-secondary btn-sm"
                        @click="store.node.openingHours.friday.push({from: '00:00', to: '00:00'})">
                  {{ $t('opening_hours_add') }}
                </button>
              </div>
            </div>
            <div class="opening-hours">
              <div v-for="(openingHour, index) in store.node.openingHours.friday"
                   :key="`opening-hours-friday-${index}-from`">
                <div class="form-row">
                  <form-group class="col-6" :label="$t('opening_hours_from')"
                              :label-for="`opening-hours-friday-${index}-from`" prepend-icon="hourglass-start"
                              :invalid-feedback="validationErrors[`openingHoursFriday${index}From`]">
                    <select :id="`opening-hours-friday-${index}-from`" class="custom-select" v-model="openingHour.from">
                      <option v-for="hour in hours" :key="hour" :value="hour">{{ hour }}</option>
                    </select>
                  </form-group>
                  <form-group class="col-6" :label="$t('opening_hours_to')"
                              :label-for="`opening-hours-friday-${index}-to`" prepend-icon="hourglass-end"
                              :invalid-feedback="validationErrors[`openingHoursFriday${index}To`]">
                    <select :id="`opening-hours-friday-${index}-to`" class="custom-select" v-model="openingHour.to">
                      <option v-for="hour in hours" :key="hour" :value="hour">{{ hour }}</option>
                    </select>
                  </form-group>
                </div>
                <button v-if="store.node.openingHours.friday.length > 1" type="button"
                        class="btn btn-sm btn-outline-danger" @click="store.node.openingHours.friday.splice(index, 1)">
                  {{ $t('opening_hours_remove') }}
                </button>
              </div>
            </div>

            <div class="opening-hours-header form-row">
              <div class="col">
                <label>{{ $t('saturday') }}</label>
              </div>
              <div class="col-auto">
                <button type="button" class="btn btn-secondary btn-sm"
                        @click="store.node.openingHours.saturday.push({from: '00:00', to: '00:00'})">
                  {{ $t('opening_hours_add') }}
                </button>
              </div>
            </div>
            <div class="opening-hours">
              <div v-for="(openingHour, index) in store.node.openingHours.saturday"
                   :key="`opening-hours-saturday-${index}-from`">
                <div class="form-row">
                  <form-group class="col-6" :label="$t('opening_hours_from')"
                              :label-for="`opening-hours-saturday-${index}-from`" prepend-icon="hourglass-start"
                              :invalid-feedback="validationErrors[`openingHoursSaturday${index}From`]">
                    <select :id="`opening-hours-saturday-${index}-from`" class="custom-select" v-model="openingHour.from">
                      <option v-for="hour in hours" :key="hour" :value="hour">{{ hour }}</option>
                    </select>
                  </form-group>
                  <form-group class="col-6" :label="$t('opening_hours_to')"
                              :label-for="`opening-hours-saturday-${index}-to`" prepend-icon="hourglass-end"
                              :invalid-feedback="validationErrors[`openingHoursSaturday${index}To`]">
                    <select :id="`opening-hours-saturday-${index}-to`" class="custom-select" v-model="openingHour.to">
                      <option v-for="hour in hours" :key="hour" :value="hour">{{ hour }}</option>
                    </select>
                  </form-group>
                </div>
                <button v-if="store.node.openingHours.saturday.length > 1" type="button"
                        class="btn btn-sm btn-outline-danger" @click="store.node.openingHours.saturday.splice(index, 1)">
                  {{ $t('opening_hours_remove') }}
                </button>
              </div>
            </div>
          </fieldset>
        </form-page>
        <footer>
          <div class="form-row">
            <div class="col-auto">
              <button :disabled="store.saving" class="btn btn-primary"
                      v-text="!store.saving ? $t('save_button') : $t('saving')"></button>
            </div>
          </div>
        </footer>
      </form>
    </article>
  </app-framework>
</template>

<style>
.opening-hours-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 5px;
}

.opening-hours-header label {
  margin-bottom: 0;
}

.opening-hours {
  padding: 5px 10px 5px;
  border-radius: 0.25rem;
  border: 1px solid #ced4da;
  background: #fff;
  margin-bottom: 1rem;
}

.opening-hours > div + div {
  margin-top: 1rem;
}

.opening-hours .form-group {
  margin-bottom: 10px;
}

.opening-hours button {
  margin-bottom: 5px;
}

.delivery-named-areas-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 5px;
}

.delivery-named-areas label {
  margin-bottom: 0;
}

.delivery-named-areas {
  padding: 5px 10px 5px;
  border-radius: 0.25rem;
  border: 1px solid #ced4da;
  background: #fff;
  margin-bottom: 1rem;
}

.delivery-named-areas > div + div {
  margin-top: 1rem;
}

.delivery-named-areas .form-group {
  margin-bottom: 10px;
}

.delivery-named-areas button {
  margin-bottom: 5px;
}
</style>

<script>
import FormMixin from "../../mixins/FormMixin";
import AppFramework from "../../components/layout/AppFramework";
import FormPage from "../../components/common/FormPage";
import FormGroup from "../../components/common/FormGroup";
import LoadingState from "../../components/states/LoadingState";
import gql from "graphql-tag";
import BreadCrumbs from "@/components/layout/BreadCrumbs";

export default {
  name: 'storeForm',
  i18nOptions: {namespaces: ['store_form', 'store_permissions', 'common', 'breadcrumbs', 'error']},
  components: {BreadCrumbs, LoadingState, FormGroup, FormPage, AppFramework},
  mixins: [FormMixin],
  metaInfo() {
    return {
      title: this.$t(this.$route.params.id === 'new' ? 'store_form_new' : 'store_form_edit')
    }
  },
  data: () => ({
    store: {
      node: null,
      loading: true,
      error: null,
      saving: false
    },
    menus: {
      nodes: null,
      loading: true,
      error: null
    },
    deliveryFeeTypes: [
      "TO_BE_NEGOTIATED",
      "FIXED",
      "BY_NAMED_AREA"
    ],
    hours: [
      "00:00", "00:30", "01:00", "01:30", "02:00", "02:30", "03:00", "03:30", "04:00", "04:30", "05:00", "05:30",
      "06:00", "06:30", "07:00", "07:30", "08:00", "08:30", "09:00", "09:30", "10:00", "10:30", "11:00", "11:30",
      "12:00", "12:30", "13:00", "13:30", "14:00", "14:30", "15:00", "15:30", "16:00", "16:30", "17:00", "17:30",
      "18:00", "18:30", "19:00", "19:30", "20:00", "20:30", "21:00", "21:30", "22:00", "22:30", "23:00", "23:30",
      "24:00"
    ]
  }),
  computed: {
    isNew: function () {
      return this.$route.params.id === "new";
    },
  },
  async mounted() {
    await Promise.all([this.queryStore(), this.queryMenus()])
    window.store = this.store
  },
  methods: {
    async tryAgain() {
      if (!this.isNew && this.store.error) {
        await Promise.all([this.queryStore(), this.queryMenus()])
      }
    },
    async queryMenus() {
      this.menus.loading = true;
      try {
        const data = await this.$apolloClient.queryAll("menus", {
          query: gql`query ($first: Int! $after: String) {
              menus(first: $first after: $after) {
                  nodes {
                      id
                      name
                  }
                  pageInfo {
                      hasNextPage
                      endCursor
                  }
              }
          }`,
          variables: {
            first: 100
          }
        });
        this.menus.nodes = data.nodes;
        this.menus.error = null;
      } catch (e) {
        this.menus.error = e.message
      }
      this.menus.loading = false;
    },
    async queryStore() {
      /* if (this.isNew) {
        this.store.node = {
          deliveryFeeType: 'TO_BE_NEGOTIATED',
          address: {
            position: {}
          },
          openingHours: {
            sunday: [{from: '00:00', to: '00:00'}],
            monday: [{from: '00:00', to: '00:00'}],
            tuesday: [{from: '00:00', to: '00:00'}],
            wednesday: [{from: '00:00', to: '00:00'}],
            thursday: [{from: '00:00', to: '00:00'}],
            friday: [{from: '00:00', to: '00:00'}],
            saturday: [{from: '00:00', to: '00:00'}],
          }
        };
        this.store.loading = false;
        return;
      } */

      this.store.loading = true;
      try {
        const response = await this.$apolloClient.query({
          query: gql`query ($id: ID!) {
            store(id: $id) {
              id
              menuId
              name
              code
              phone
              preparationTimeInMinutes
              acceptDeliveryOrder
              acceptPickupOrder
              paymentMethods {
                name
                askNeedChange
                fixedPriceIncrease
                percentagePriceIncrease
              }
              deliveryFeeType
              deliveryFeeFixed
              deliveryFeeByNamedArea {
                name
                price
              }
              freeDeliveryStartingFrom
              minimumOrderValue
              address {
                position {
                  latitude
                  longitude
                }
                countryId
                line1
                houseNumber
                line2
                level3
                postalCode
                level2
                level1
                instructions
              }
              openingHours {
                sunday { from to }
                monday { from to }
                tuesday { from to }
                wednesday { from to }
                thursday { from to }
                friday { from to }
                saturday { from to }
              }
            }
          }`,
          variables: {
            id: this.$route.params.id
          },
          fetchPolicy: "network-only",
        });
        this.store.node = response.data.store;
        if (!this.store.node.deliveryFeeType) {
          this.store.node.deliveryFeeType = 'TO_BE_NEGOTIATED'
        }
        if (!this.store.node.paymentMethods || !this.store.node.paymentMethods.length) {
          this.store.node.paymentMethods = [
            {name: '', askNeedChange: false, fixedPriceIncrease: 0, percentagePriceIncrease: 0}
          ]
        }
        this.store.node.paymentMethods = this.store.node.paymentMethods.map(m => {
          if (m.priceChangeOption) {
            return m
          }
          let priceChangeOption = "NO_CHANGE"
          let fixedPriceIncrease = 0
          let percentagePriceIncrease = ""
          if (m.fixedPriceIncrease && m.fixedPriceIncrease > 0) {
            priceChangeOption = 'FIXED_INCREASE'
            fixedPriceIncrease = m.fixedPriceIncrease
          } else if (m.fixedPriceIncrease && m.fixedPriceIncrease < 0) {
            priceChangeOption = 'FIXED_DISCOUNT'
            fixedPriceIncrease = -1 * m.fixedPriceIncrease
          } else if (m.percentagePriceIncrease && m.percentagePriceIncrease > 0) {
            priceChangeOption = 'PERCENTAGE_INCREASE'
            percentagePriceIncrease = (m.percentagePriceIncrease * 100).toString()
          } else if (m.percentagePriceIncrease && m.percentagePriceIncrease < 0) {
            priceChangeOption = 'PERCENTAGE_DISCOUNT'
            percentagePriceIncrease = (-1 * m.percentagePriceIncrease * 100).toString()
          }
          return {name: m.name, askNeedChange: m.askNeedChange, priceChangeOption, fixedPriceIncrease, percentagePriceIncrease}
        })
        if (!this.store.node.deliveryFeeByNamedArea) {
          this.store.node.deliveryFeeByNamedArea = [
            {name: '', price: 0}
          ]
        }
        if (!this.store.node.address) {
          this.store.node.address = {
            position: {}
          }
        }
        if (!this.store.node.address.position) {
          this.store.node.address.position = {}
        }
        if (!this.store.node.openingHours) {
          this.store.node.openingHours = {
            sunday: [{from: '00:00', to: '00:00'}],
            monday: [{from: '00:00', to: '00:00'}],
            tuesday: [{from: '00:00', to: '00:00'}],
            wednesday: [{from: '00:00', to: '00:00'}],
            thursday: [{from: '00:00', to: '00:00'}],
            friday: [{from: '00:00', to: '00:00'}],
            saturday: [{from: '00:00', to: '00:00'}],
          }
        }

        this.store.error = null;
      } catch (e) {
        this.store.error = e.message
      }
      this.store.loading = false;
    },
    async saveStore() {
      if (this.store.saving || !this.checkForm()) {
        return;
      }
      this.disableForm();
      this.store.saving = true;
      try {
        if (this.isNew) {
          await this.createStore();
        } else {
          await this.updateStore();
        }
        await this.$apolloClient.resetStore();
        await this.$router.push('/stores');
      } catch (e) {
        this.store.saving = false;
        this.activeForm();
        this.showError(e);
      }
    },
    async createStore() {
      await this.$apolloClient.mutate({
        mutation: gql`mutation ($input: StoreInput!) {
          createStore(input: $input) {
            id
            menuId
            name
            code
            phone
            preparationTimeInMinutes
            acceptDeliveryOrder
            acceptPickupOrder
            paymentMethods {
              name
              askNeedChange
              fixedPriceIncrease
              percentagePriceIncrease
            }
            deliveryFeeType
            deliveryFeeFixed
            deliveryFeeByNamedArea {
              name
              price
            }
            freeDeliveryStartingFrom
            minimumOrderValue
            address {
              position {
                latitude
                longitude
              }
              countryId
              line1
              houseNumber
              line2
              level3
              postalCode
              level2
              level1
              instructions
            }
            openingHours {
              sunday { from to }
              monday { from to }
              tuesday { from to }
              wednesday { from to }
              thursday { from to }
              friday { from to }
              saturday { from to }
            }
          }
        }`,
        variables: {
          input: {
            menuId: this.store.node.menuId,
            name: this.store.node.name,
            code: this.store.node.code,
            phone: this.store.node.phone || null,
            preparationTimeInMinutes: this.store.node.preparationTimeInMinutes || null,
            acceptDeliveryOrder: this.store.node.acceptDeliveryOrder,
            acceptPickupOrder: this.store.node.acceptPickupOrder,
            paymentMethods: this.store.node.paymentMethods.map(m => {
              let fixedPriceIncrease = null
              let percentagePriceIncrease = null
              switch (m.priceChangeOption) {
                case 'FIXED_INCREASE':
                  if (m.fixedPriceIncrease) {
                    fixedPriceIncrease = m.fixedPriceIncrease
                  }
                  break
                case 'FIXED_DISCOUNT':
                  if (m.fixedPriceIncrease) {
                    fixedPriceIncrease = -1 * m.fixedPriceIncrease
                  }
                  break
                case 'PERCENTAGE_INCREASE':
                  if (m.percentagePriceIncrease) {
                    percentagePriceIncrease = parseInt(m.percentagePriceIncrease, 10) / 100
                  }
                  break
                case 'PERCENTAGE_DISCOUNT':
                  if (m.percentagePriceIncrease) {
                    percentagePriceIncrease = -1 * parseInt(m.percentagePriceIncrease, 10) / 100
                  }
                  break
              }
              return {name: m.name, askNeedChange: m.askNeedChange, fixedPriceIncrease, percentagePriceIncrease}
            }),
            deliveryFeeType: this.store.node.deliveryFeeType,
            deliveryFeeFixed: this.store.node.deliveryFeeFixed || null,
            deliveryFeeByNamedArea: this.store.node.deliveryFeeByNamedArea.map(a => {return {name: a.name, price: a.price}}),
            freeDeliveryStartingFrom: this.store.node.freeDeliveryStartingFrom,
            minimumOrderValue: this.store.node.minimumOrderValue,
            address: {
              position: ((this.store.node.address.position.latitude && this.store.node.address.position.longitude) && {
                latitude: this.store.node.address.position.latitude,
                longitude: this.store.node.address.position.longitude
              }) || null,
              countryId: this.store.node.address.countryId,
              line1: this.store.node.address.line1,
              houseNumber: this.store.node.address.houseNumber,
              line2: this.store.node.address.line2,
              level3: this.store.node.address.level3,
              postalCode: this.store.node.address.postalCode,
              level2: this.store.node.address.level2,
              level1: this.store.node.address.level1,
              instructions: this.store.node.address.instructions,
            },
            openingHours: {
              sunday: this.store.node.openingHours.sunday.map(r => {return {from: r.from, to: r.to}}),
              monday: this.store.node.openingHours.monday.map(r => {return {from: r.from, to: r.to}}),
              tuesday: this.store.node.openingHours.tuesday.map(r => {return {from: r.from, to: r.to}}),
              wednesday: this.store.node.openingHours.wednesday.map(r => {return {from: r.from, to: r.to}}),
              thursday: this.store.node.openingHours.thursday.map(r => {return {from: r.from, to: r.to}}),
              friday: this.store.node.openingHours.friday.map(r => {return {from: r.from, to: r.to}}),
              saturday: this.store.node.openingHours.saturday.map(r => {return {from: r.from, to: r.to}})
            }
          }
        }
      });
    },
    async updateStore() {
      await this.$apolloClient.mutate({
        mutation: gql`mutation ($id: ID! $input: StoreInput!) {
          updateStore(id: $id input: $input) {
            id
            menuId
            name
            code
            phone
            preparationTimeInMinutes
            acceptDeliveryOrder
            acceptPickupOrder
            paymentMethods {
              name
              askNeedChange
              fixedPriceIncrease
              percentagePriceIncrease
            }
            deliveryFeeType
            deliveryFeeFixed
            deliveryFeeByNamedArea {
              name
              price
            }
            freeDeliveryStartingFrom
            minimumOrderValue
            address {
              position {
                latitude
                longitude
              }
              countryId
              line1
              houseNumber
              line2
              level3
              postalCode
              level2
              level1
              instructions
            }
            openingHours {
              sunday { from to }
              monday { from to }
              tuesday { from to }
              wednesday { from to }
              thursday { from to }
              friday { from to }
              saturday { from to }
            }
          }
        }`,
        variables: {
          id: this.$route.params.id,
          input: {
            menuId: this.store.node.menuId,
            name: this.store.node.name,
            code: this.store.node.code,
            phone: this.store.node.phone || null,
            preparationTimeInMinutes: this.store.node.preparationTimeInMinutes || null,
            acceptDeliveryOrder: this.store.node.acceptDeliveryOrder,
            acceptPickupOrder: this.store.node.acceptPickupOrder,
            paymentMethods: this.store.node.paymentMethods.map(m => {
              let fixedPriceIncrease = null
              let percentagePriceIncrease = null
              switch (m.priceChangeOption) {
                case 'FIXED_INCREASE':
                  if (m.fixedPriceIncrease) {
                    fixedPriceIncrease = m.fixedPriceIncrease
                  }
                  break
                case 'FIXED_DISCOUNT':
                  if (m.fixedPriceIncrease) {
                    fixedPriceIncrease = -1 * m.fixedPriceIncrease
                  }
                  break
                case 'PERCENTAGE_INCREASE':
                  if (m.percentagePriceIncrease) {
                    percentagePriceIncrease = parseInt(m.percentagePriceIncrease, 10) / 100
                  }
                  break
                case 'PERCENTAGE_DISCOUNT':
                  if (m.percentagePriceIncrease) {
                    percentagePriceIncrease = -1 * parseInt(m.percentagePriceIncrease, 10) / 100
                  }
                  break
              }
              return {name: m.name, askNeedChange: m.askNeedChange, fixedPriceIncrease, percentagePriceIncrease}
            }),
            deliveryFeeType: this.store.node.deliveryFeeType,
            deliveryFeeFixed: this.store.node.deliveryFeeFixed || null,
            deliveryFeeByNamedArea: this.store.node.deliveryFeeByNamedArea.map(a => {return {name: a.name, price: a.price}}),
            freeDeliveryStartingFrom: this.store.node.freeDeliveryStartingFrom,
            minimumOrderValue: this.store.node.minimumOrderValue,
            address: {
              position: ((this.store.node.address.position.latitude && this.store.node.address.position.longitude) && {
                latitude: this.store.node.address.position.latitude,
                longitude: this.store.node.address.position.longitude
              }) || null,
              countryId: this.store.node.address.countryId,
              line1: this.store.node.address.line1,
              houseNumber: this.store.node.address.houseNumber,
              line2: this.store.node.address.line2,
              level3: this.store.node.address.level3,
              postalCode: this.store.node.address.postalCode,
              level2: this.store.node.address.level2,
              level1: this.store.node.address.level1,
              instructions: this.store.node.address.instructions,
            },
            openingHours: {
              sunday: this.store.node.openingHours.sunday.map(r => {return {from: r.from, to: r.to}}),
              monday: this.store.node.openingHours.monday.map(r => {return {from: r.from, to: r.to}}),
              tuesday: this.store.node.openingHours.tuesday.map(r => {return {from: r.from, to: r.to}}),
              wednesday: this.store.node.openingHours.wednesday.map(r => {return {from: r.from, to: r.to}}),
              thursday: this.store.node.openingHours.thursday.map(r => {return {from: r.from, to: r.to}}),
              friday: this.store.node.openingHours.friday.map(r => {return {from: r.from, to: r.to}}),
              saturday: this.store.node.openingHours.saturday.map(r => {return {from: r.from, to: r.to}})
            }
          }
        }
      });
    },
    validateForm() {
      this.validateFormMixin();
    }
  }
}
</script>
